import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import BottomSheet from '../base/BottomSheet';
import { useCompanyLogo } from '@/hooks/useCompanyLogo';
import { supabase } from '@/lib/supabase';

interface MoreItem {
  to: string;
  label: string;
  icon: string;
  section: string;
}

const allItems: MoreItem[] = [
  { to: '/admin/appointments', label: 'Appointments', icon: 'ri-calendar-check-line', section: 'Agenda' },
  { to: '/admin/calendar', label: 'Calendar', icon: 'ri-calendar-line', section: 'Agenda' },
  { to: '/admin/sessions', label: 'Sesiones Pre-evento', icon: 'ri-camera-line', section: 'Agenda' },
  { to: '/admin/clients', label: 'Clients', icon: 'ri-team-line', section: 'Ventas' },
  { to: '/admin/client-tokens', label: 'Client Tokens', icon: 'ri-key-line', section: 'Ventas' },
  { to: '/admin/contracts', label: 'Contracts', icon: 'ri-file-text-line', section: 'Ventas' },
  { to: '/admin/contract-templates', label: 'Contract Templates', icon: 'ri-file-copy-line', section: 'Ventas' },
  { to: '/admin/packages', label: 'Packages', icon: 'ri-box-3-line', section: 'Ventas' },
  { to: '/admin/individual-products', label: 'Individual Products', icon: 'ri-shopping-bag-3-line', section: 'Ventas' },
  { to: '/admin/payments', label: 'Payments', icon: 'ri-money-dollar-circle-line', section: 'Ventas' },
  { to: '/admin/precios', label: 'Cotizador Online', icon: 'ri-price-tag-3-line', section: 'Ventas' },
  { to: '/admin/service-config', label: 'Service Config', icon: 'ri-settings-4-line', section: 'Config' },
  { to: '/admin/produccion', label: 'Producción', icon: 'ri-route-line', section: 'Operaciones' },
  { to: '/admin/entregas', label: 'Entregas', icon: 'ri-download-cloud-line', section: 'Operaciones' },
  { to: '/admin/storage-manager', label: 'Storage Manager', icon: 'ri-hard-drive-2-line', section: 'Operaciones' },
  { to: '/admin/email-campaigns', label: 'Email Campaigns', icon: 'ri-mail-send-line', section: 'Marketing' },
  { to: '/admin/site-content', label: 'Site Content', icon: 'ri-pages-line', section: 'Config' },
  { to: '/admin/print-templates', label: 'Print Templates', icon: 'ri-printer-line', section: 'Config' },
  { to: '/admin/print-approvals', label: 'Print Approvals', icon: 'ri-check-double-line', section: 'Config' },
  { to: '/admin/samples/photos', label: 'Sample Photos', icon: 'ri-image-line', section: 'Muestras' },
  { to: '/admin/samples/video', label: 'Sample Videos', icon: 'ri-video-line', section: 'Muestras' },
  { to: '/admin/help-videos', label: 'Help Videos', icon: 'ri-question-answer-line', section: 'Muestras' },
  { to: '/admin/ruleta-premios', label: 'Ruleta Premios', icon: 'ri-gift-line', section: 'Marketing' },
  { to: '/admin/invitations', label: 'Invitations', icon: 'ri-coupon-line', section: 'Herramientas' },
  { to: '/admin/dvds', label: 'DVDs', icon: 'ri-folder-music-line', section: 'Herramientas' },
  { to: '/admin/tv-apps', label: 'Apps de TV', icon: 'ri-smartphone-line', section: 'Herramientas' },
  { to: '/admin/dvd-builder', label: 'DVD Builder', icon: 'ri-disc-line', section: 'Herramientas' },
  { to: '/admin/pose-guides', label: 'Pose Guides', icon: 'ri-book-open-line', section: 'Herramientas' },
  { to: '/admin/photobooks', label: 'Photobooks', icon: 'ri-book-2-line', section: 'Herramientas' },
  { to: '/admin/photobook-frames', label: 'Photobook Frames', icon: 'ri-layout-masonry-line', section: 'Herramientas' },
  { to: '/admin/event-attention', label: 'Event Attention', icon: 'ri-map-pin-5-line', section: 'Herramientas' },
  { to: '/admin/settings', label: 'Settings', icon: 'ri-settings-3-line', section: 'Config' },
  { to: '/admin/settings/smtp2go', label: 'SMTP2GO', icon: 'ri-mail-settings-line', section: 'Config' },
  { to: '/admin/settings/tracking-pixels', label: 'Tracking Pixels', icon: 'ri-code-s-slash-line', section: 'Config' },
  { to: '/admin/system-auditor', label: 'System Auditor', icon: 'ri-shield-check-line', section: 'Config' },
  { to: '/admin/studioflow', label: 'StudioFlow Dashboard', icon: 'ri-robot-2-line', section: 'AI Production' },
  { to: '/admin/studioflow/workflows', label: 'Workflows', icon: 'ri-git-branch-line', section: 'AI Production' },
  { to: '/admin/studioflow/photoshop', label: 'Photoshop', icon: 'ri-image-edit-line', section: 'AI Production' },
  { to: '/admin/studioflow/lightroom', label: 'Lightroom', icon: 'ri-contrast-2-line', section: 'AI Production' },
  { to: '/admin/studioflow/resolve', label: 'DaVinci Resolve', icon: 'ri-film-line', section: 'AI Production' },
  { to: '/admin/studioflow/files', label: 'File Manager', icon: 'ri-folder-2-line', section: 'AI Production' },
  { to: '/admin/studioflow/logs', label: 'Activity Logs', icon: 'ri-history-line', section: 'AI Production' },
  { to: '/admin/studioflow/connections', label: 'Connections', icon: 'ri-plug-line', section: 'AI Production' },
  { to: '/admin/studioflow/settings', label: 'StudioFlow Settings', icon: 'ri-settings-5-line', section: 'AI Production' },
];

const sectionOrder = ['Agenda', 'Ventas', 'Operaciones', 'Marketing', 'Herramientas', 'Muestras', 'Config', 'AI Production'];

interface AdminMoreSheetProps {
  open: boolean;
  onClose: () => void;
}

export default function AdminMoreSheet({ open, onClose }: AdminMoreSheetProps) {
  const [search, setSearch] = useState('');
  const navigate = useNavigate();
  const location = useLocation();
  const companyLogo = useCompanyLogo();

  const isItemActive = (to: string): boolean => {
    if (location.pathname === to) return true;
    if (location.pathname.startsWith(to + '/')) return true;
    return false;
  };

  const handleNavigate = (to: string) => {
    onClose();
    navigate(to);
  };

  const handleLogout = async () => {
    onClose();
    await supabase.auth.signOut();
    navigate('/admin/login');
  };

  const filteredItems = search.trim()
    ? allItems.filter((item) =>
        item.label.toLowerCase().includes(search.toLowerCase()) ||
        item.section.toLowerCase().includes(search.toLowerCase())
      )
    : allItems;

  const grouped = filteredItems.reduce<Record<string, MoreItem[]>>((acc, item) => {
    if (!acc[item.section]) acc[item.section] = [];
    acc[item.section].push(item);
    return acc;
  }, {});

  const visibleSections = search.trim()
    ? Object.keys(grouped).sort((a, b) => sectionOrder.indexOf(a) - sectionOrder.indexOf(b))
    : sectionOrder.filter((s) => grouped[s]);

  return (
    <BottomSheet
      open={open}
      onClose={onClose}
      title="Más opciones"
      showSearch={true}
      searchPlaceholder="Buscar módulos..."
      onSearch={setSearch}
      maxHeight="80vh"
    >
      {/* Logo / Header */}
      <div className="px-4 py-3 flex items-center gap-2 border-b border-gray-100">
        <div className="w-8 h-8 flex items-center justify-center">
          <img
            src={companyLogo || 'https://fotosyvideosantarosa.com/storage/2025/12/fyvnueva2.png'}
            alt="Logo"
            className="w-full h-full object-contain"
          />
        </div>
        <span className="text-sm font-medium text-gray-700">Pixory AI</span>
      </div>

      {/* Sections */}
      <div className="px-3 sm:px-4 py-2">
        {visibleSections.map((section) => (
          <div key={section} className="mb-4">
            <div className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2 px-1">
              {section}
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5 sm:gap-2">
              {grouped[section].map((item) => (
                <button
                  key={item.to}
                  onClick={() => handleNavigate(item.to)}
                  className={`flex flex-col items-center justify-center gap-1.5 p-2 sm:p-3 min-h-[64px] sm:min-h-[72px] rounded-xl transition-colors cursor-pointer ${
                    isItemActive(item.to)
                      ? 'bg-teal-50 ring-1 ring-teal-200'
                      : 'bg-gray-50 hover:bg-teal-50 active:bg-teal-100'
                  }`}
                >
                  <div className={`w-8 h-8 sm:w-9 sm:h-9 flex items-center justify-center ${isItemActive(item.to) ? 'text-teal-700' : 'text-teal-600'}`}>
                    <i className={`${item.icon} text-lg sm:text-xl`}></i>
                  </div>
                  <span className={`text-[10px] sm:text-xs font-medium text-center leading-tight ${isItemActive(item.to) ? 'text-teal-700' : 'text-gray-700'}`}>
                    {item.label}
                  </span>
                </button>
              ))}
            </div>
          </div>
        ))}

        {filteredItems.length === 0 && (
          <div className="text-center py-8 text-gray-400 text-sm">
            <i className="ri-search-line text-2xl mb-2 block"></i>
            No se encontraron módulos
          </div>
        )}
      </div>

      {/* Footer / Logout */}
      <div className="px-3 sm:px-4 py-3 border-t border-gray-100">
        <button
          onClick={handleLogout}
          className="flex items-center gap-3 w-full px-3 sm:px-4 py-3 min-h-[52px] text-red-600 hover:bg-red-50 rounded-xl transition-colors cursor-pointer"
        >
          <div className="w-9 h-9 flex items-center justify-center bg-red-50 rounded-xl">
            <i className="ri-logout-box-line text-lg"></i>
          </div>
          <span className="text-sm font-semibold">Cerrar sesión</span>
        </button>
      </div>
    </BottomSheet>
  );
}